/**
 * Ciao — Floating AI Assistant Widget
 *
 * FAB icon (bottom-right) → opens centered modal chat.
 * Only visible for superadmins (rol_id === 1).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IoSparkles } from 'react-icons/io5';
import CiaoChat from './components/CiaoChat';
import './Ciao.scss';

const Ciao: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showForAllUsers] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsExpanded(false);
  }, []);

  // Escape key: exit fullscreen first, then close chat
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isExpanded) setIsExpanded(false);
        else handleClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isExpanded, handleClose]);

  const userJson = localStorage.getItem('user');
  const isAdmin = userJson ? JSON.parse(userJson).is_admin === true : false;

  if (!showForAllUsers && !isAdmin) return null;

  const portalTarget = document.getElementById('root') || document.body;

  return createPortal(
    <div className="ciao-root">
      {/* Modal overlay + chat */}
      {isOpen && (
        <div
          className="ciao-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isExpanded ? 'var(--background-color, #fff)' : 'rgba(0, 0, 0, 0.45)',
            backdropFilter: isExpanded ? 'none' : 'blur(4px)',
            padding: isExpanded ? 0 : 16,
          }}
          onClick={(e) => {
            if (!isExpanded && e.target === e.currentTarget) handleClose();
          }}
        >
          <CiaoChat
            onClose={handleClose}
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded((prev) => !prev)}
          />
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={`ciao-fab ${isOpen ? 'ciao-fab--open' : ''}`}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 10002,
          width: 52,
          height: 52,
          borderRadius: 16,
          background: '#FC7556',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        title="Ciao — Asistente IA"
      >
        <IoSparkles style={{ fontSize: 22, transition: 'transform 0.2s', transform: isOpen ? 'rotate(15deg)' : 'none' }} />
      </button>
    </div>,
    portalTarget
  );
};

export default Ciao;
